import React, { useState, useEffect } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { FaChevronLeft, FaChevronRight, FaTrash, FaBook } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import { resolveImageUrl } from '../utils/urlResolver';
import { getCache, setCache, createCacheKey } from '../utils/cache';

const ArtHistoryAppreciationScroller = ({ activities, onActivityClick, onActivityDoubleClick, onDeleteActivity }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [fullActivities, setFullActivities] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadFullActivities = async () => {
      setLoading(true);
      try {
        const loaded = await Promise.all(
          activities.map(async (activity) => {
            // Check cache first
            const cacheKey = createCacheKey('userActivity', { activityId: activity.id });
            const cached = getCache(cacheKey);
            if (cached) {
              return cached;
            }
            
            try {
              const docRef = doc(db, 'userActivities', activity.id);
              const docSnap = await getDoc(docRef);
              if (docSnap.exists()) {
                const fullActivity = { id: docSnap.id, ...docSnap.data() };
                // Cache the full activity (5 min cache)
                setCache(cacheKey, fullActivity, 5 * 60 * 1000);
                return fullActivity;
              }
              return activity;
            } catch (error) {
              console.error('Error loading activity:', error);
              return activity;
            }
          })
        );
        setFullActivities(loaded);
      } catch (error) {
        console.error('Error loading activities:', error);
        setFullActivities(activities);
      } finally {
        setLoading(false);
      }
    };

    if (activities.length > 0) {
      loadFullActivities();
    } else {
      setFullActivities([]);
      setLoading(false);
    }
  }, [activities]);

  const currentActivity = fullActivities[currentIndex];
  const hasNext = currentIndex < fullActivities.length - 1;
  const hasPrev = currentIndex > 0;

  const handleNext = () => {
    if (hasNext) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  const handlePrev = () => {
    if (hasPrev) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  const handleDelete = (e, activity) => {
    e.stopPropagation();
    if (window.confirm('Are you sure you want to delete this art history appreciation?')) {
      onDeleteActivity(activity);
      // Adjust index if needed
      if (currentIndex >= fullActivities.length - 1 && currentIndex > 0) {
        setCurrentIndex(currentIndex - 1);
      }
    }
  };

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600"></div>
      </div>
    );
  }

  if (fullActivities.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <FaBook className="w-12 h-12 mx-auto mb-2 opacity-50" />
        <p>No art history appreciations to display</p>
      </div>
    );
  }

  const artworkMeta = currentActivity?.metadata?.artworkMeta || {};
  const appreciation = currentActivity?.appreciation || [];
  const points = currentActivity?.points || [];

  return (
    <div className="relative">
      {/* Navigation */}
      <div className="flex items-center justify-between mb-3 md:mb-4">
        <button
          onClick={handlePrev}
          disabled={!hasPrev}
          className={`p-1.5 md:p-2 rounded-lg transition-all ${
            hasPrev
              ? 'bg-amber-100 text-amber-600 hover:bg-amber-200'
              : 'bg-gray-100 text-gray-300 cursor-not-allowed'
          }`}
        >
          <FaChevronLeft className="w-4 h-4 md:w-5 md:h-5" />
        </button>

        <div className="text-center flex-1 px-2 min-w-0">
          <span className="text-xs md:text-sm text-gray-600">
            {currentIndex + 1} of {fullActivities.length}
          </span>
          <p className="text-[10px] md:text-xs text-gray-500 mt-1 truncate">
            {artworkMeta.title || 'Untitled Artwork'}
          </p>
        </div>

        <button
          onClick={handleNext}
          disabled={!hasNext}
          className={`p-1.5 md:p-2 rounded-lg transition-all ${
            hasNext
              ? 'bg-amber-100 text-amber-600 hover:bg-amber-200'
              : 'bg-gray-100 text-gray-300 cursor-not-allowed'
          }`}
        >
          <FaChevronRight className="w-4 h-4 md:w-5 md:h-5" />
        </button>
      </div>

      {/* Activity Card */}
      <AnimatePresence mode="wait">
        <motion.div
          key={currentActivity.id}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          transition={{ duration: 0.3 }}
          className="bg-white rounded-lg md:rounded-xl p-3 md:p-6 shadow-lg border-2 border-amber-200 cursor-pointer hover:shadow-xl transition-shadow"
          onClick={() => onActivityClick(currentActivity)}
          onDoubleClick={(e) => {
            e.stopPropagation();
            if (onActivityDoubleClick) {
              onActivityDoubleClick(currentActivity);
            }
          }}
        >
          {/* Header */}
          <div className="flex items-start justify-between mb-3 md:mb-4">
            <div className="flex-1 min-w-0 pr-2">
              <h4 className="font-bold text-base md:text-lg text-gray-800 mb-1">
                {artworkMeta.title || 'Untitled Artwork'}
              </h4>
              <p className="text-xs md:text-sm text-gray-600">
                {artworkMeta.artist || 'Unknown Artist'}
                {artworkMeta.movement && ` • ${artworkMeta.movement}`}
              </p>
            </div>
            <button
              onClick={(e) => handleDelete(e, currentActivity)}
              className="p-1.5 md:p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors flex-shrink-0"
              title="Delete appreciation"
            >
              <FaTrash className="w-3 h-3 md:w-4 md:h-4" />
            </button>
          </div>

          {/* Artwork Image */}
          {currentActivity.originalImageUrl && (
            <div className="mb-3 md:mb-4 rounded-lg overflow-hidden border-2 border-amber-200">
              <img
                src={resolveImageUrl(currentActivity.originalImageUrl)}
                alt={artworkMeta.title || 'Artwork'}
                className="w-full h-32 md:h-48 object-cover"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            </div>
          )}

          {/* Appreciation Points Preview */}
          <div className="space-y-1.5 md:space-y-2">
            {(appreciation.length > 0 ? appreciation : points).slice(0, 2).map((point, idx) => (
              <div key={idx} className="text-xs md:text-sm text-gray-700 line-clamp-2">
                {typeof point === 'string' ? point : (point?.text || point?.label || '')}
              </div>
            ))}
            {((appreciation.length > 0 ? appreciation : points).length > 2) && (
              <p className="text-[10px] md:text-xs text-amber-600 font-medium">
                +{((appreciation.length > 0 ? appreciation : points).length - 2)} more points
              </p>
            )}
          </div>

          {/* Date */}
          {currentActivity.createdAt && (() => {
            let date;
            try {
              if (currentActivity.createdAt?.toDate) {
                date = currentActivity.createdAt.toDate();
              } else if (currentActivity.createdAt?._seconds) {
                date = new Date(currentActivity.createdAt._seconds * 1000);
              } else if (typeof currentActivity.createdAt === 'number') {
                date = new Date(currentActivity.createdAt);
              } else if (typeof currentActivity.createdAt === 'string') {
                date = new Date(currentActivity.createdAt);
              } else {
                date = new Date(currentActivity.createdAt);
              }
              
              // Check if date is valid
              if (isNaN(date.getTime())) {
                return null;
              }
              
              return (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <p className="text-xs text-gray-500">
                    {date.toLocaleDateString()}
                  </p>
                </div>
              );
            } catch (error) {
              console.error('Error formatting date:', error);
              return null;
            }
          })()}
        </motion.div>
      </AnimatePresence>

      {/* Dots indicator */}
      {fullActivities.length > 1 && (
        <div className="flex justify-center gap-2 mt-4">
          {fullActivities.map((_, idx) => (
            <button
              key={idx}
              onClick={() => setCurrentIndex(idx)}
              className={`w-2 h-2 rounded-full transition-all ${
                idx === currentIndex ? 'bg-amber-600 w-6' : 'bg-gray-300'
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default ArtHistoryAppreciationScroller;

